/**
 * Localization API Client with React Query Hooks
 *
 * Provides query and mutation hooks for word list localization management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from 'services/api';
import logger from 'utils/logger';
import type {
  WordListLocalization,
  WordListLocalizationCreate,
  WordListLocalizationUpdate,
  GeneratedLocalization,
  WordListLocalizationApiResponse,
  GeneratedLocalizationApiResponse,
} from '../types/localization.types';
import {
  mapLocalizationFromApi,
  mapGeneratedLocalizationFromApi,
  mapLocalizationCreateToApi,
  mapLocalizationUpdateToApi,
} from '../types/localization.types';

// =============================================================================
// Query Keys
// =============================================================================

export const localizationKeys = {
  all: ['localizations'] as const,
  list: (listId: string) => [...localizationKeys.all, 'list', listId] as const,
  detail: (listId: string, languageCode: string) =>
    [...localizationKeys.all, 'detail', listId, languageCode] as const,
};

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get all localizations for a word list
 */
const getLocalizations = async (listId: string): Promise<WordListLocalization[]> => {
  const data = await apiGet<WordListLocalizationApiResponse[]>(
    `/api/admin/word-lists/${listId}/localizations`
  );
  return data.map(mapLocalizationFromApi);
};

/**
 * Get a specific localization
 */
const getLocalization = async (
  listId: string,
  languageCode: string
): Promise<WordListLocalization> => {
  const data = await apiGet<WordListLocalizationApiResponse>(
    `/api/admin/word-lists/${listId}/localizations/${languageCode}`
  );
  return mapLocalizationFromApi(data);
};

/**
 * Create a new localization
 */
const createLocalization = async (
  listId: string,
  data: WordListLocalizationCreate
): Promise<WordListLocalization> => {
  const result = await apiPost<WordListLocalizationApiResponse>(
    `/api/admin/word-lists/${listId}/localizations`,
    mapLocalizationCreateToApi(data)
  );
  return mapLocalizationFromApi(result);
};

/**
 * Update an existing localization
 */
const updateLocalization = async (
  listId: string,
  languageCode: string,
  data: WordListLocalizationUpdate
): Promise<WordListLocalization> => {
  const result = await apiPut<WordListLocalizationApiResponse>(
    `/api/admin/word-lists/${listId}/localizations/${languageCode}`,
    mapLocalizationUpdateToApi(data)
  );
  return mapLocalizationFromApi(result);
};

/**
 * Delete a localization
 */
const deleteLocalization = async (listId: string, languageCode: string): Promise<void> => {
  await apiDelete<void>(`/api/admin/word-lists/${listId}/localizations/${languageCode}`);
};

/**
 * Generate localizations using LLM
 */
const generateLocalizations = async (
  listId: string,
  targetLanguages: string[]
): Promise<GeneratedLocalization[]> => {
  const data = await apiPost<{ localizations: GeneratedLocalizationApiResponse[] }>(
    `/api/admin/word-lists/${listId}/localizations/generate`,
    { target_languages: targetLanguages }
  );
  return data.localizations.map(mapGeneratedLocalizationFromApi);
};

/**
 * Bulk set localizations (replaces all localizations for the list)
 */
const setLocalizationsBulk = async (
  listId: string,
  localizations: WordListLocalizationCreate[]
): Promise<WordListLocalization[]> => {
  const data = await apiPut<WordListLocalizationApiResponse[]>(
    `/api/admin/word-lists/${listId}/localizations`,
    { localizations: localizations.map(mapLocalizationCreateToApi) }
  );
  return data.map(mapLocalizationFromApi);
};

// =============================================================================
// React Query Hooks
// =============================================================================

/**
 * Query hook for getting all localizations
 */
export const useLocalizations = (listId: string) => {
  return useQuery({
    queryKey: localizationKeys.list(listId),
    queryFn: () => getLocalizations(listId),
    enabled: !!listId,
  });
};

/**
 * Query hook for getting a specific localization
 */
export const useLocalization = (listId: string, languageCode: string) => {
  return useQuery({
    queryKey: localizationKeys.detail(listId, languageCode),
    queryFn: () => getLocalization(listId, languageCode),
    enabled: !!listId && !!languageCode,
  });
};

/**
 * Mutation hook for creating a localization
 */
export const useCreateLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: WordListLocalizationCreate }) =>
      createLocalization(listId, data),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: localizationKeys.list(listId) });
    },
    onError: (error) => {
      logger.error('Create localization error:', error);
    },
  });
};

/**
 * Mutation hook for updating a localization
 */
export const useUpdateLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      languageCode,
      data,
    }: {
      listId: string;
      languageCode: string;
      data: WordListLocalizationUpdate;
    }) => updateLocalization(listId, languageCode, data),
    onSuccess: (_, { listId, languageCode }) => {
      queryClient.invalidateQueries({ queryKey: localizationKeys.list(listId) });
      queryClient.invalidateQueries({
        queryKey: localizationKeys.detail(listId, languageCode),
      });
    },
    onError: (error) => {
      logger.error('Update localization error:', error);
    },
  });
};

/**
 * Mutation hook for deleting a localization
 */
export const useDeleteLocalization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, languageCode }: { listId: string; languageCode: string }) =>
      deleteLocalization(listId, languageCode),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: localizationKeys.list(listId) });
    },
    onError: (error) => {
      logger.error('Delete localization error:', error);
    },
  });
};

/**
 * Mutation hook for generating localizations with LLM
 */
export const useGenerateLocalizations = () => {
  return useMutation({
    mutationFn: ({ listId, targetLanguages }: { listId: string; targetLanguages: string[] }) =>
      generateLocalizations(listId, targetLanguages),
    onError: (error) => {
      logger.error('Generate localizations error:', error);
    },
  });
};

/**
 * Generate draft localizations (stateless, no list ID required)
 */
const generateDraftLocalizations = async (
  name: string,
  description: string | undefined,
  sourceLanguage: string,
  targetLanguages: string[]
): Promise<GeneratedLocalization[]> => {
  const data = await apiPost<{ localizations: GeneratedLocalizationApiResponse[] }>(
    '/api/admin/word-lists/localizations/generate-draft',
    {
      name,
      description: description || null,
      source_language: sourceLanguage,
      target_languages: targetLanguages,
    }
  );
  return data.localizations.map(mapGeneratedLocalizationFromApi);
};

/**
 * Mutation hook for generating draft localizations (stateless)
 */
export const useGenerateDraftLocalizations = () => {
  return useMutation({
    mutationFn: ({
      name,
      description,
      sourceLanguage,
      targetLanguages,
    }: {
      name: string;
      description?: string;
      sourceLanguage: string;
      targetLanguages: string[];
    }) => generateDraftLocalizations(name, description, sourceLanguage, targetLanguages),
    onError: (error) => {
      logger.error('Generate draft localizations error:', error);
    },
  });
};

/**
 * Mutation hook for bulk setting localizations
 */
export const useSetLocalizationsBulk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      localizations,
    }: {
      listId: string;
      localizations: WordListLocalizationCreate[];
    }) => setLocalizationsBulk(listId, localizations),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: localizationKeys.list(listId) });
    },
    onError: (error) => {
      logger.error('Set localizations bulk error:', error);
    },
  });
};
